import {
  API_BASE_PATH,
  ERROR_CODES,
  HEADER_CLIENT_ID,
  HEADER_CSRF,
  type ApiEnvelope,
  type ErrorCode,
  type FieldIssue,
  type PageMeta,
} from '@taskflow/shared';
import { config } from '../config/env';

/**
 * A stable id for this browser tab, minted once per page load.
 *
 * Sent on every mutation so the realtime layer can attribute the resulting events back
 * to the tab that caused them. `crypto.randomUUID` is available in every browser this
 * app targets.
 */
export const CLIENT_ID = crypto.randomUUID();

/**
 * The single HTTP boundary.
 *
 * Every request in the application goes through here, which is what makes the
 * following true everywhere rather than per-call:
 *   • credentials are always sent, so cookie auth works
 *   • the CSRF token is attached to every unsafe method
 *   • the response envelope is unwrapped exactly once
 *   • failures always arrive as an ApiError carrying a machine-readable code
 *
 * Note what is NOT here: retries. A blind retry on a failed POST can duplicate a
 * card. Retrying is TanStack Query's job, configured per query, and mutations
 * that need it will send an Idempotency-Key.
 */

export class ApiError extends Error {
  readonly code: ErrorCode;
  readonly status: number;
  readonly details: FieldIssue[] | undefined;
  readonly requestId: string | undefined;

  constructor(options: {
    code: ErrorCode;
    message: string;
    status: number;
    details?: FieldIssue[];
    requestId?: string;
  }) {
    super(options.message);
    this.name = 'ApiError';
    this.code = options.code;
    this.status = options.status;
    this.details = options.details;
    this.requestId = options.requestId;
  }

  /** True when the user is not (or no longer) signed in. */
  get isAuthError(): boolean {
    return this.code === ERROR_CODES.UNAUTHENTICATED || this.code === ERROR_CODES.SESSION_EXPIRED;
  }

  /** Field-level messages keyed by form field, ready for React Hook Form. */
  get fieldErrors(): Record<string, string> {
    const output: Record<string, string> = {};
    for (const issue of this.details ?? []) output[issue.path] = issue.message;
    return output;
  }
}

export interface RequestOptions {
  method?: 'GET' | 'POST' | 'PATCH' | 'PUT' | 'DELETE';
  body?: unknown;
  query?: Record<string, string | number | boolean | undefined>;
  signal?: AbortSignal;
  /** Sent as Idempotency-Key so a retried create cannot duplicate a record. */
  idempotencyKey?: string;
  /** Sent as If-Match for optimistic concurrency on updates. */
  etag?: string;
}

export interface ApiResult<T> {
  data: T;
  page: PageMeta | undefined;
  requestId: string;
}

const UNSAFE_METHODS = new Set(['POST', 'PATCH', 'PUT', 'DELETE']);

/** Reads the CSRF cookie for the double-submit check the Worker performs. */
function readCsrfToken(): string | null {
  const match = document.cookie.match(/(?:^|;\s*)__Host-tf_csrf=([^;]+)/);
  return match?.[1] ? decodeURIComponent(match[1]) : null;
}

function buildUrl(path: string, query?: RequestOptions['query']): string {
  const url = `${config.apiBaseUrl}${API_BASE_PATH}${path}`;
  if (!query) return url;

  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(query)) {
    if (value !== undefined) params.set(key, String(value));
  }
  const serialised = params.toString();
  return serialised ? `${url}?${serialised}` : url;
}

/**
 * Single-flight refresh.
 *
 * When an access token expires, several queries usually fail at once. Without this,
 * each would fire its own `/auth/refresh` — and because refresh ROTATES the token,
 * the second call would present an already-consumed one, which the server correctly
 * treats as theft and revokes the entire session family. Users would be
 * mysteriously signed out whenever two requests raced.
 *
 * So: at most one refresh in flight, and every caller awaits the same promise.
 */
let refreshInFlight: Promise<boolean> | null = null;

async function refreshSession(): Promise<boolean> {
  refreshInFlight ??= (async () => {
    try {
      const response = await fetch(`${config.apiBaseUrl}${API_BASE_PATH}/auth/refresh`, {
        method: 'POST',
        credentials: 'include',
        headers: { Accept: 'application/json' },
      });
      return response.ok;
    } catch {
      return false;
    }
  })().finally(() => {
    // Cleared on the next tick, so everyone who awaited this attempt sees the same
    // result before another one can start.
    setTimeout(() => {
      refreshInFlight = null;
    }, 0);
  });

  return refreshInFlight;
}

/** Called when the session is definitively gone, so the app redirects exactly once. */
type SessionExpiredHandler = () => void;
let onSessionExpired: SessionExpiredHandler | null = null;

export function setSessionExpiredHandler(handler: SessionExpiredHandler | null): void {
  onSessionExpired = handler;
}

/**
 * Performs a request, transparently refreshing an expired session once.
 *
 * The retry is deliberately narrow: only on 401, only once, and never for the auth
 * endpoints themselves — retrying `/auth/login` after a 401 would just repeat a
 * wrong password and burn through the lockout counter.
 */
export async function request<T>(
  path: string,
  options: RequestOptions = {},
): Promise<ApiResult<T>> {
  try {
    return await send<T>(path, options);
  } catch (error) {
    const isAuthEndpoint = path.startsWith('/auth/');

    if (error instanceof ApiError && error.isAuthError && !isAuthEndpoint) {
      if (await refreshSession()) return send<T>(path, options);
      onSessionExpired?.();
    }

    throw error;
  }
}

async function send<T>(path: string, options: RequestOptions): Promise<ApiResult<T>> {
  const method = options.method ?? 'GET';
  const headers = new Headers({ Accept: 'application/json' });

  // FormData sets its own multipart Content-Type (with a boundary), so it must NOT be
  // JSON-encoded or given a JSON content type — doing either corrupts the upload.
  const isFormData = typeof FormData !== 'undefined' && options.body instanceof FormData;
  if (options.body !== undefined && !isFormData) headers.set('Content-Type', 'application/json');
  if (options.idempotencyKey) headers.set('Idempotency-Key', options.idempotencyKey);
  if (options.etag) headers.set('If-Match', options.etag);

  if (UNSAFE_METHODS.has(method)) {
    const csrf = readCsrfToken();
    if (csrf) headers.set(HEADER_CSRF, csrf);
    // Identifies this tab, so the realtime layer can tell this tab's own change apart
    // from the same person's change in another tab — the first ignores the echo, the
    // second applies it.
    headers.set(HEADER_CLIENT_ID, CLIENT_ID);
  }

  let response: Response;
  try {
    response = await fetch(buildUrl(path, options.query), {
      method,
      headers,
      // Cookie auth: without this the browser sends no session at all.
      credentials: 'include',
      ...(options.body !== undefined
        ? { body: isFormData ? (options.body as FormData) : JSON.stringify(options.body) }
        : {}),
      ...(options.signal ? { signal: options.signal } : {}),
    });
  } catch (error) {
    // Offline, DNS failure, or an aborted request. There is no envelope to read.
    if (error instanceof DOMException && error.name === 'AbortError') throw error;
    throw new ApiError({
      code: ERROR_CODES.SERVICE_UNAVAILABLE,
      message: 'Could not reach the server. Check your connection and try again.',
      status: 0,
    });
  }

  if (response.status === 204) {
    return {
      data: undefined as T,
      page: undefined,
      requestId: response.headers.get('X-Request-Id') ?? '',
    };
  }

  let envelope: ApiEnvelope<T>;
  try {
    envelope = (await response.json()) as ApiEnvelope<T>;
  } catch {
    // A non-JSON response means something upstream of the Worker answered —
    // a Cloudflare error page, for instance. Report it honestly.
    throw new ApiError({
      code: ERROR_CODES.INTERNAL_ERROR,
      message: 'The server returned an unreadable response.',
      status: response.status,
      ...(response.headers.get('X-Request-Id')
        ? { requestId: response.headers.get('X-Request-Id') as string }
        : {}),
    });
  }

  if (!envelope.success) {
    throw new ApiError({
      code: envelope.error.code,
      message: envelope.error.message,
      status: response.status,
      ...(envelope.error.details ? { details: envelope.error.details } : {}),
      requestId: envelope.error.requestId,
    });
  }

  return {
    data: envelope.data,
    page: envelope.meta.page,
    requestId: envelope.meta.requestId,
  };
}

/** Convenience wrappers — the shape most call sites want. */
export const http = {
  get: <T>(path: string, options?: Omit<RequestOptions, 'method' | 'body'>) =>
    request<T>(path, { ...options, method: 'GET' }).then((result) => result.data),

  getPage: <T>(path: string, options?: Omit<RequestOptions, 'method' | 'body'>) =>
    request<T>(path, { ...options, method: 'GET' }),

  post: <T>(path: string, body?: unknown, options?: Omit<RequestOptions, 'method' | 'body'>) =>
    request<T>(path, { ...options, method: 'POST', body }).then((result) => result.data),

  patch: <T>(path: string, body?: unknown, options?: Omit<RequestOptions, 'method' | 'body'>) =>
    request<T>(path, { ...options, method: 'PATCH', body }).then((result) => result.data),

  delete: <T>(path: string, options?: Omit<RequestOptions, 'method' | 'body'>) =>
    request<T>(path, { ...options, method: 'DELETE' }).then((result) => result.data),
};
